import { useQuery } from '@tanstack/react-query'
import { stats } from '@/lib/api'
import { FortniteIcon } from '@/components/PlatformIcons'

const MODE_LABELS: Record<string, string> = {
  overall: 'Overall',
  solo: 'Solo',
  duo: 'Duos',
  squad: 'Squads',
  ltm: 'LTM',
  ballistic: 'Ballistic',
}

export function FortniteStats() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stats', 'fortnite'],
    queryFn: async () => {
      const result = await stats.getFortnite()
      return result.data as any
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]" role="status" aria-busy="true">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-[#9d4dbb] border-t-transparent mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading Fortnite stats...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FortniteIcon size={40} color="#9d4dbb" />
          <h2 className="font-display font-bold text-xl mt-4 mb-2">
            {error ? 'Failed to Load Stats' : 'Fortnite Not Connected'}
          </h2>
          <p className="text-gray-400 text-sm mb-5">
            {error
              ? 'Something went wrong fetching your stats.'
              : 'Connect your Epic account to see stats.'}
          </p>
          {error && (
            <button
              onClick={() => refetch()}
              className="px-5 py-2 bg-[#9d4dbb] text-white rounded-lg text-sm font-bold hover:bg-[#b05fd4] transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    )
  }

  const overall = data.stats?.overall ?? {}
  const allModes = data.stats || {}
  const battlePass = data.battlePass

  const hoursPlayed = Math.floor((overall.minutesPlayed ?? 0) / 60)
  const minutesRemaining = (overall.minutesPlayed ?? 0) % 60
  const updatedAtLabel = formatUpdatedAt(data._cachedAt)

  const modes = Object.entries(allModes)
    .filter(([key, value]) => key !== 'overall' && value && typeof value === 'object')
    .map(([key, value]) => ({ key, label: formatModeLabel(key), data: value as any }))
    .filter((m) => (m.data.matches ?? 0) > 0)

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      {/* Profile Header */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-800 p-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#9d4dbb]/20 flex items-center justify-center shrink-0">
            <FortniteIcon size={24} color="#9d4dbb" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl">{data.accountName || data.username || 'Fortnite'}</h1>
            <p className="text-gray-500 text-sm">Fortnite Battle Royale</p>
            <p className="text-gray-600 text-xs mt-1">{updatedAtLabel}</p>
          </div>
        </div>
        {battlePass && (
          <div className="bg-gray-800 rounded-lg px-4 py-2 border border-gray-700 text-center">
            <p className="text-gray-500 text-xs uppercase tracking-wider">Battle Pass</p>
            <p className="font-display font-bold text-xl text-[#9d4dbb]">
              Lv. {battlePass.level}
            </p>
          </div>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Wins" value={(overall.wins ?? 0).toLocaleString()} />
        <StatCard label="Eliminations" value={(overall.kills ?? 0).toLocaleString()} />
        <StatCard label="K/D Ratio" value={(overall.kd ?? 0).toFixed(2)} />
        <StatCard label="Win Rate" value={`${(overall.winRate ?? 0).toFixed(1)}%`} />
      </div>

      {/* Extended Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <MiniCard label="Total Matches" value={(overall.matches ?? 0).toLocaleString()} />
        <MiniCard label="Score" value={(overall.score ?? 0).toLocaleString()} />
        <MiniCard label="Score/Match" value={Math.round(overall.scorePerMatch ?? 0).toLocaleString()} />
        <MiniCard label="Kills/Match" value={(overall.killsPerMatch ?? 0).toFixed(2)} />
        <MiniCard label="Players Outlived" value={(overall.playersOutlived ?? 0).toLocaleString()} />
        <MiniCard label="Deaths" value={(overall.deaths ?? 0).toLocaleString()} />
      </div>

      {/* Mode Breakdown */}
      {modes.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-display font-bold text-lg text-gray-300">Mode Breakdown</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {modes.map((mode) => (
              <ModeCard key={mode.key} label={mode.label} stats={mode.data} />
            ))}
          </div>
        </div>
      )}

      {/* Performance Summary */}
      <div className="space-y-4">
        <h2 className="font-display font-bold text-lg text-gray-300">Performance Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            label="Best Mode"
            value={getBestMode(data.stats)}
          />
          <SummaryCard
            label="Total Eliminations"
            value={(overall.kills ?? 0).toLocaleString()}
          />
          <SummaryCard
            label="Avg Players Outlived"
            value={`${Math.round((overall.playersOutlived ?? 0) / Math.max(overall.matches ?? 1, 1))}/match`}
          />
          <SummaryCard
            label="Time Played"
            value={`${hoursPlayed}h ${minutesRemaining}m`}
          />
        </div>
      </div>
    </div>
  )
}

function formatUpdatedAt(cachedAt?: number): string {
  if (!cachedAt) return 'Updated time unavailable'
  const updatedAt = new Date(cachedAt * 1000)
  const now = Date.now()
  const diffSeconds = Math.max(0, Math.floor((now - updatedAt.getTime()) / 1000))

  if (diffSeconds < 60) return 'Updated just now'
  if (diffSeconds < 3600) return `Updated ${Math.floor(diffSeconds / 60)}m ago`
  if (diffSeconds < 86400) return `Updated ${Math.floor(diffSeconds / 3600)}h ago`
  return `Updated ${updatedAt.toLocaleDateString()}`
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#9d4dbb]/30 transition-colors p-5">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className="font-display font-bold text-2xl text-[#9d4dbb]">{value}</p>
    </div>
  )
}

function MiniCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#9d4dbb]/30 transition-colors p-3 text-center">
      <p className="font-bold text-white text-sm">{value}</p>
      <p className="text-gray-500 text-xs mt-0.5">{label}</p>
    </div>
  )
}

function ModeCard({ label, stats }: { label: string; stats: any }) {
  const kd = stats?.kd ?? 0
  const kdColor = kd >= 2.0 ? 'text-green-400' : 'text-white'

  return (
    <div className="bg-gray-800/50 rounded-xl border border-[#9d4dbb]/20 hover:border-[#9d4dbb]/40 transition-colors p-5">
      <div className="flex items-center gap-2 mb-4">
        <FortniteIcon size={16} color="#9d4dbb" />
        <h3 className="font-display font-bold text-base">{label}</h3>
      </div>
      <div className="grid grid-cols-3 gap-x-4 gap-y-3">
        <div>
          <p className="text-gray-500 text-xs">Wins</p>
          <p className="font-bold text-[#9d4dbb]">{(stats?.wins ?? 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Kills</p>
          <p className="font-bold text-white">{(stats?.kills ?? 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">K/D</p>
          <p className={`font-bold ${kdColor}`}>{kd.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Matches</p>
          <p className="font-bold text-white">{(stats?.matches ?? 0).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Win %</p>
          <p className="font-bold text-white">{(stats?.winRate ?? 0).toFixed(1)}%</p>
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#9d4dbb]/30 transition-colors p-5 text-center">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className="font-display font-bold text-lg text-[#9d4dbb]">{value}</p>
    </div>
  )
}

function formatModeLabel(modeKey: string): string {
  if (MODE_LABELS[modeKey]) return MODE_LABELS[modeKey]
  return modeKey
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

function getBestMode(modeStats: any): string {
  if (!modeStats) return 'N/A'

  const candidates = Object.entries(modeStats)
    .filter(([key]) => key !== 'overall')
    .map(([key, value]) => ({
      name: formatModeLabel(key),
      winRate: (value as any)?.winRate ?? 0,
      matches: (value as any)?.matches ?? 0,
    }))
    .filter((m) => m.matches >= 5)

  if (candidates.length === 0) return 'N/A'

  const best = candidates.reduce((a, b) => (a.winRate > b.winRate ? a : b))
  return `${best.name} (${best.winRate.toFixed(1)}%)`
}
